import { useEffect, useRef, useState } from "react";

export default function AutoComplete({
  options,
  selected,
  onSelect,
  labelFunction,
  className = "",
  disabled = false,
  clearOnSelect,
  disabledMessage,
  maxItems = 50
}) {
  const [hidden, setHidden] = useState(true);
  const [defaultVal, setDefaultValue] = useState(
    selected?.id ? labelFunction(selected) : ""
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const handleSelect = (option) => {
    if (disabled) return;
    setDefaultValue(labelFunction(option));
    setHidden(true);
    setHighlightedIndex(-1);
    if (onSelect) onSelect(option);
    if (clearOnSelect) setDefaultValue("");
  };

  const filteredOptions = options
    .filter((option) =>
      labelFunction(option).toLowerCase().includes(defaultVal.toLowerCase())
    )
    .slice(0, maxItems === "none" ? Infinity : maxItems);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setHidden(true);
      }
    };

    if (!disabled) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [disabled]);

  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      optionRefs.current[highlightedIndex] &&
      dropdownRef.current
    ) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  return (
    <div
      className={`relative ${className} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      id="hs-combobox-basic-usage"
      data-hs-combo-box=""
    >
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={defaultVal}
          disabled={disabled}
          onChange={(e) => {
            setDefaultValue(e.target.value);
            setHighlightedIndex(0);
            setHidden(false);
          }}
          onFocus={() => !disabled && setHidden(false)}
          onKeyDown={(e) => {
            if (disabled || hidden) return;

            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightedIndex((prev) =>
                prev < filteredOptions.length - 1 ? prev + 1 : 0
              );
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredOptions.length - 1
              );
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (
                highlightedIndex >= 0 &&
                highlightedIndex < filteredOptions.length
              ) {
                handleSelect(filteredOptions[highlightedIndex]);
              }
            }
          }}
          role="combobox"
          aria-expanded={!hidden}
          aria-controls="autocomplete-options"
          className="py-2.5 sm:py-3 ps-4 pe-9 block w-full border-gray-200 rounded-lg sm:text-sm focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)] disabled:opacity-20 disabled:bg-gray-500 disabled:border-gray-900 disabled:pointer-events-none"
          data-hs-combo-box-input=""
        />

        <div
          className="absolute top-1/2 end-3 -translate-y-1/2 cursor-pointer"
          role="button"
          data-hs-combo-box-toggle=""
          onClick={() => {
            if (!disabled) setHidden(!hidden);
          }}
        >
          <svg
            className="shrink-0 size-3.5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m7 15 5 5 5-5" />
            <path d="m7 9 5-5 5 5" />
          </svg>
        </div>

        {disabled && disabledMessage && (
          <div className="mt-1 font-semibold text-xs text-red-500 italic px-1">
            {disabledMessage}
          </div>
        )}
      </div>

      {!hidden && !disabled && (
        <div
          ref={dropdownRef}
          role="listbox"
          className="absolute z-50 w-full max-h-72 p-1 bg-white border border-gray-200 rounded-lg overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300"
          data-hs-combo-box-output=""
        >
          {filteredOptions.map((option, index) => {
            const isHighlighted = index === highlightedIndex;
            return (
              <div
                aria-selected={selected?.id === option?.id}
                key={option?.id}
                ref={(el) => (optionRefs.current[index] = el)}
                onClick={() => handleSelect(option)}
                role="option"
                option={option}
                tabIndex="0"
                className={`cursor-pointer py-2 px-4 w-full text-sm text-gray-800 rounded-lg focus:outline-none ${
                  isHighlighted ? "bg-gray-200" : "hover:bg-gray-100"
                }`}
              >
                <div className="flex justify-between items-center w-full">
                  <span>{labelFunction(option)}</span>
                  {selected?.id === option?.id && (
                    <svg
                      className="shrink-0 size-3.5 text-[var(--primary-color)]"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
